import { useState, useEffect, useCallback } from "react";
import axios from "auth/FetchInterceptor";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN } from "constants/AuthConstant";

/**
 * Convert a base64url VAPID key to Uint8Array (required by PushManager.subscribe).
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/**
 * Get a ready ServiceWorkerRegistration with a timeout.
 * Registers the SW explicitly if no controller is active yet.
 */
async function getReadyRegistration(timeoutMs = 10000) {
  if (!navigator.serviceWorker.controller) {
    try {
      await navigator.serviceWorker.register("/service-worker.js", {
        scope: "/",
      });
    } catch {
      // fall through — .ready may still resolve
    }
  }
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) =>
      setTimeout(
        () =>
          reject(
            new Error(
              `Service Worker did not become ready within ${timeoutMs / 1000}s.`,
            ),
          ),
        timeoutMs,
      ),
    ),
  ]);
}

/**
 * Hook — manages Web Push subscription lifecycle for the BO app.
 */
export default function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /** Authenticated axios helper */
  const authHeaders = useCallback(() => {
    const token = localStorage.getItem(AUTH_TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  /** Re-check permission + existing subscription. */
  const refreshState = useCallback(() => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      !("serviceWorker" in navigator)
    )
      return;

    setPermission(Notification.permission);

    getReadyRegistration(5000)
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setIsSubscribed(!!sub))
      .catch(() => {});
  }, []);

  // Initial support check
  useEffect(() => {
    const supported =
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;

    setIsSupported(supported);
    if (supported) refreshState();
  }, [refreshState]);

  // Re-check on tab focus + Permissions API change event
  useEffect(() => {
    if (!isSupported) return;

    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshState();
    };
    document.addEventListener("visibilitychange", onVisibility);

    let permStatus;
    navigator.permissions
      ?.query({ name: "notifications" })
      .then((status) => {
        permStatus = status;
        status.addEventListener("change", refreshState);
      })
      .catch(() => {});

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      permStatus?.removeEventListener("change", refreshState);
    };
  }, [isSupported, refreshState]);

  /** Request permission → subscribe via PushManager → save to backend. */
  const subscribe = useCallback(async () => {
    if (!isSupported) return;
    setIsLoading(true);
    setError(null);

    try {
      // 1. Request browser permission
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") {
        setError("Permission refusée par l'utilisateur.");
        setIsLoading(false);
        return;
      }

      // 2. Fetch VAPID public key
      const { data: vapidData } = await axios.get(
        `${API_BASE_URL}/push/vapid-key`,
      );
      const applicationServerKey = urlBase64ToUint8Array(vapidData.publicKey);

      // 3. Subscribe via PushManager
      const registration = await getReadyRegistration(15000);
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // 4. Save to backend
      const subJSON = pushSubscription.toJSON();
      await axios.post(
        `${API_BASE_URL}/push/subscribe`,
        { endpoint: subJSON.endpoint, keys: subJSON.keys },
        { headers: authHeaders() },
      );

      setIsSubscribed(true);
    } catch (err) {
      console.error("[usePushNotifications] subscribe error:", err);
      setError(err.message ?? "Erreur lors de l'abonnement aux notifications.");
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, authHeaders]);

  /** Unsubscribe from PushManager and remove from backend. */
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;
    setIsLoading(true);
    setError(null);

    try {
      const registration = await getReadyRegistration(10000);
      const sub = await registration.pushManager.getSubscription();

      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await axios.post(
          `${API_BASE_URL}/push/unsubscribe`,
          { endpoint },
          { headers: authHeaders() },
        );
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error("[usePushNotifications] unsubscribe error:", err);
      setError(err.message ?? "Erreur lors de la désinscription.");
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, authHeaders]);

  /**
   * Sync existing subscription with backend.
   * Useful when account switching to re-associate endpoint with the new BO user.
   */
  const syncSubscription = useCallback(async () => {
    if (!isSupported) return;
    try {
      const registration = await getReadyRegistration(10000);
      const pushSubscription = await registration.pushManager.getSubscription();

      if (pushSubscription) {
        console.log(
          "[usePushNotifications] Syncing existing subscription with backend...",
        );
        const subscriptionJSON = pushSubscription.toJSON();
        await axios.post(
          `${API_BASE_URL}/push/subscribe`,
          {
            endpoint: subscriptionJSON.endpoint,
            keys: subscriptionJSON.keys,
          },
          { headers: authHeaders() },
        );
        setIsSubscribed(true);
      }
    } catch (err) {
      console.warn(
        "[usePushNotifications] syncSubscription failed:",
        err.message,
      );
    }
  }, [isSupported, authHeaders]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    syncSubscription,
  };
}
